import { FormField, IconFormField, TextLink } from '@ai-tutor/ui';
import { Lock, Mail, User } from 'lucide-react';
import type { Control } from 'react-hook-form';

import { AuthMode, PASSWORD_HINT } from '@/lib/schemas/auth';

import type { AuthFormValues } from './useAuthSubmit';

/**
 * The two mode-specific lines that hang off the field stack.
 *
 * "Forgot your password?" sits directly under the password field, where someone
 * who has just failed to remember it is already looking - not in the card
 * footer below the sign-up prompt, which is where the old form kept it.
 *
 * The password rule is stated before submission rather than discovered as an
 * error after a failed attempt.
 */
function FieldExtras({
  mode,
  onForgotPassword,
}: {
  mode: AuthMode;
  onForgotPassword: () => void;
}) {
  if (mode === 'login') {
    return (
      <div className='flex justify-end'>
        {/* `tap-target`: a 17px-tall line, tapped as 44 on a phone. Its row
            has 17px clear above and below, so the tap area overlaps nothing. */}
        <TextLink
          size='label'
          onClick={onForgotPassword}
          className='tap-target'
        >
          Forgot your password?
        </TextLink>
      </div>
    );
  }
  if (mode === 'register') {
    return <p className='text-label text-muted-foreground'>{PASSWORD_HINT}</p>;
  }
  return null;
}

/**
 * The fields for a given auth mode.
 *
 * `name={... as never}` throughout: `AuthForm` drives one `useForm` across
 * three different value shapes, so react-hook-form's `Path<T>` cannot see
 * `fullName` on the login union member. The alternative is three separate
 * `useForm` calls and three copies of this markup, which is how the modes drift
 * apart. The zod resolver is the real guard on what each mode accepts.
 */
export function AuthFormFields({
  mode,
  control,
  onForgotPassword,
}: {
  mode: AuthMode;
  control: Control<AuthFormValues>;
  onForgotPassword: () => void;
}) {
  return (
    <>
      {mode === 'register' && (
        <FormField
          control={control}
          name={'fullName' as never}
          render={({ field }) => (
            <IconFormField
              icon={User}
              label='Full name'
              autoComplete='name'
              field={field}
            />
          )}
        />
      )}

      <FormField
        control={control}
        name={'email' as never}
        render={({ field }) => (
          <IconFormField
            icon={Mail}
            label='Email'
            type='email'
            inputMode='email'
            autoComplete='email'
            placeholder='you@example.com'
            field={field}
          />
        )}
      />

      {mode !== 'reset' && (
        <FormField
          control={control}
          name={'password' as never}
          render={({ field }) => (
            <IconFormField
              icon={Lock}
              label='Password'
              type='password'
              autoComplete={
                mode === 'login' ? 'current-password' : 'new-password'
              }
              field={field}
            />
          )}
        />
      )}

      <FieldExtras mode={mode} onForgotPassword={onForgotPassword} />
    </>
  );
}
